import { useCallback, useEffect, useRef } from "react";

/**
 * Tracks one in-flight RTK thunk request at a time, identified by a caller-supplied
 * key, so a slow fetch is **superseded** rather than raced or duplicated.
 *
 * Returns a stable `request(key, start)`:
 *
 * - same `key` already in flight → returns the existing promise, **no second
 *   dispatch**
 * - different `key` in flight → `.abort()`s it, then starts yours
 * - nothing in flight → starts yours
 *
 * plus an abort on unmount, which also forgets the key so a remount starts afresh.
 *
 * Why not the usual `useEffect` cleanup-abort? Because these listing effects
 * re-run for reasons that have nothing to do with the request identity.
 * `ListProductComponent`'s effect dispatches the counts *and* the products and
 * lists `hasInitialCount` / `hasFilteredCount` among its deps, so a count landing
 * mid-flight re-runs it — cleanup-abort would cancel and restart the products
 * request on every cold load. Keying on the request's own identity is what makes
 * "did the target actually change?" answerable.
 *
 * It also closes a pre-existing hole: those same re-runs used to fire a **second
 * identical** products request while the first was still open, because the only
 * guard was `!hasProducts` and the cache doesn't fill until the first one lands.
 *
 * The key should be the request's full identity. For paged product fetches that's
 * exactly the `productsByPage` cache key — it already encodes id, page size, sort,
 * filters and page.
 *
 * Requires the thunk to honour cancellation (take `signal` from thunkAPI and pass
 * it to axios), and its reducer to ignore `meta.aborted` rather than treating it as
 * an error. See `fetchProductsBy` in `redux/slices/productsSlice.js`.
 *
 * @example
 * const request = useSupersedingRequest();
 * useEffect(() => {
 *   if (!hasProducts) request(key, () => dispatch(fetchProductsBy({ ... })));
 * }, [request, key, hasProducts, dispatch]);
 */
export default function useSupersedingRequest() {
  const inFlight = useRef(null);

  // Don't leave a request running after the user navigates away. Forget it too:
  // StrictMode re-runs effects straight after this cleanup, and a remembered key
  // would make that re-run return the aborted promise instead of starting a new one.
  useEffect(
    () => () => {
      inFlight.current?.promise.abort();
      inFlight.current = null;
    },
    []
  );

  return useCallback((key, start) => {
    if (inFlight.current?.key === key) return inFlight.current.promise;

    inFlight.current?.promise.abort();

    const promise = start();
    inFlight.current = { key, promise };

    // Clear once settled so a *failed* request can be retried by a later re-run.
    // Guarded because a superseded request settles after its replacement was
    // recorded, and must not wipe it.
    promise.finally(() => {
      if (inFlight.current?.promise === promise) inFlight.current = null;
    });

    return promise;
  }, []);
}
